import React, { useState, useEffect, useCallback } from 'react';
import { Button, Skeleton, FormModal } from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import SubNavigation from '../../components/admin/SubNavigation';
import {
  Settings,
  PhoneAndroid,
  Apple,
  Edit,
  Warning,
  History,
  CheckCircle,
  Update,
  NewReleases
} from '../../mui-icons';
import './AppVersionsPage.css';

const AppVersionsPage = () => {
  const { canEdit } = useAdminAuth();
  
  // States
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // History State
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [editForm, setEditForm] = useState({
    current_version: '',
    minimum_version: '',
    recommended_version: '',
    changelog: '',
    change_reason: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Load versions
  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await adminAPI.getAppVersions();
      
      if (response.status === 'success') {
        setVersions(response.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar versões:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // Load history
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await adminAPI.getAppVersionsHistory({ limit: 20 });
      if (response.status === 'success') {
        setHistory(response.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Toggle history view
  const toggleHistory = () => {
    if (!showHistory && history.length === 0) {
      loadHistory();
    }
    setShowHistory(!showHistory);
  };

  // Open edit modal
  const openEditModal = (version) => {
    setSelectedPlatform(version);
    setEditForm({
      current_version: version.current_version || '',
      minimum_version: version.minimum_version || '',
      recommended_version: version.recommended_version || '',
      changelog: version.changelog || '',
      change_reason: ''
    });
    setEditError('');
    setShowEditModal(true);
  };

  // Handle edit form change
  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  // Submit edit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');

    try {
      // Validate versions
      const versionRegex = /^\d+\.\d+\.\d+$/;
      if (!versionRegex.test(editForm.current_version)) {
        throw new Error('Versão atual inválida. Use o formato X.Y.Z');
      }
      if (!versionRegex.test(editForm.minimum_version)) {
        throw new Error('Versão mínima inválida. Use o formato X.Y.Z');
      }
      if (!versionRegex.test(editForm.recommended_version)) {
        throw new Error('Versão recomendada inválida. Use o formato X.Y.Z');
      }

      const response = await adminAPI.updateAppVersion(selectedPlatform.platform, editForm);
      
      if (response.status === 'success') {
        setShowEditModal(false);
        loadVersions();
        // Reload history if visible
        if (showHistory) {
          loadHistory();
        }
      }
    } catch (err) {
      console.error('Erro ao atualizar versão:', err);
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Format date
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get platform icon
  const getPlatformIcon = (platform) => {
    if (platform === 'ios') {
      return <Apple sx={{ fontSize: 32 }} />;
    }
    return <PhoneAndroid sx={{ fontSize: 32 }} />;
  };

  // Get platform name
  const getPlatformName = (platform) => {
    return platform === 'ios' ? 'iOS' : 'Android';
  };

  // Sub-navigation items
  const subNavItems = [
    { path: '/admin/app-versions', label: 'Versionamento', icon: <Update sx={{ fontSize: 18 }} /> }
  ];

  // Loading skeleton
  if (loading) {
    return (
      <div className="app-versions-page">
        <SubNavigation items={subNavItems} />
        <div className="page-header">
          <Skeleton width={200} height={32} borderRadius={8} />
        </div>
        <div className="versions-grid">
          {[1, 2].map(i => (
            <div key={i} className="admin-card pn-card pn-card--default version-card">
              <Skeleton width="100%" height={200} borderRadius={8} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="app-versions-page">
      {/* Sub Navigation */}
      <SubNavigation items={subNavItems} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <Settings sx={{ fontSize: 32 }} />
          <div>
            <h1>Versionamento do App</h1>
            <p>Gerencie as versões do aplicativo para iOS e Android</p>
          </div>
        </div>
        <div className="page-actions">
          <Button
            variant={showHistory ? 'primary' : 'secondary'}
            onClick={toggleHistory}
          >
            <History sx={{ fontSize: 18 }} />
            {showHistory ? 'Ocultar Histórico' : 'Ver Histórico'}
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="admin-card pn-card pn-card--default error-card">
          <Warning sx={{ fontSize: 48 }} />
          <p>{error}</p>
          <Button variant="primary" onClick={loadVersions}>
            Tentar novamente
          </Button>
        </div>
      )}

      {/* Versions Grid */}
      {!error && (
        <div className="versions-grid">
          {versions.map((version) => (
            <div key={version.platform} className="admin-card pn-card pn-card--default version-card">
              <div className="version-card-header">
                <div className={`platform-icon ${version.platform}`}>
                  {getPlatformIcon(version.platform)}
                </div>
                <div className="platform-info">
                  <h3>{getPlatformName(version.platform)}</h3>
                  <span className={`status-badge ${version.is_active ? 'status-active' : 'status-inactive'}`}>
                    {version.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                {canEdit() && (
                  <Button
                    variant="secondary"
                    className="pn-btn--icon"
                    onClick={() => openEditModal(version)}
                    title="Editar versão"
                  >
                    <Edit sx={{ fontSize: 18 }} />
                  </Button>
                )}
              </div>

              <div className="version-details">
                <div className="version-row">
                  <div className="version-item">
                    <label>
                      <NewReleases sx={{ fontSize: 16 }} />
                      Versão Atual
                    </label>
                    <span className="version-number current">{version.current_version}</span>
                  </div>
                </div>

                <div className="version-row">
                  <div className="version-item">
                    <label>
                      <Warning sx={{ fontSize: 16 }} />
                      Versão Mínima
                    </label>
                    <span className="version-number minimum">{version.minimum_version}</span>
                  </div>
                  <div className="version-item">
                    <label>
                      <CheckCircle sx={{ fontSize: 16 }} />
                      Recomendada
                    </label>
                    <span className="version-number recommended">{version.recommended_version}</span>
                  </div>
                </div>

                {version.changelog && (
                  <div className="changelog-preview">
                    <label>Changelog</label>
                    <pre>{version.changelog}</pre>
                  </div>
                )}

                <div className="version-footer">
                  <span className="updated-at">
                    Atualizado em: {formatDateTime(version.updated_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {versions.length === 0 && (
            <div className="admin-card pn-card pn-card--default empty-state-card">
              <Settings sx={{ fontSize: 64 }} className="empty-state-icon" />
              <h3>Nenhuma versão configurada</h3>
              <p>Entre em contato com o administrador do sistema.</p>
            </div>
          )}
        </div>
      )}

      {/* History Section */}
      {showHistory && (
        <div className="admin-card pn-card pn-card--default history-section">
          <div className="history-header">
            <h3>
              <History sx={{ fontSize: 20 }} />
              Histórico de Alterações
            </h3>
          </div>

          {historyLoading ? (
            <div className="history-loading">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} width="100%" height={72} borderRadius={8} style={{ marginBottom: 12 }} />
              ))}
            </div>
          ) : history.length === 0 ? (
            <div className="history-empty">
              <p>Nenhuma alteração registrada</p>
            </div>
          ) : (
            <div className="history-list">
              {history.map((item) => (
                <div key={item.idappversionhistory} className="history-item">
                  <div className="history-item-header">
                    <div className={`platform-badge ${item.platform}`}>
                      {getPlatformIcon(item.platform)}
                      <span>{getPlatformName(item.platform)}</span>
                    </div>
                    <span className="history-date">{formatDateTime(item.changed_at)}</span>
                  </div>
                  
                  <div className="history-changes">
                    {item.previous_version !== item.new_version && (
                      <div className="change-item">
                        <span className="change-label">Versão:</span>
                        <span className="change-old">{item.previous_version}</span>
                        <span className="change-arrow">→</span>
                        <span className="change-new">{item.new_version}</span>
                      </div>
                    )}
                    {item.previous_minimum_version !== item.new_minimum_version && (
                      <div className="change-item">
                        <span className="change-label">Mínima:</span>
                        <span className="change-old">{item.previous_minimum_version}</span>
                        <span className="change-arrow">→</span>
                        <span className="change-new">{item.new_minimum_version}</span>
                      </div>
                    )}
                    {item.previous_recommended_version !== item.new_recommended_version && (
                      <div className="change-item">
                        <span className="change-label">Recomendada:</span>
                        <span className="change-old">{item.previous_recommended_version}</span>
                        <span className="change-arrow">→</span>
                        <span className="change-new">{item.new_recommended_version}</span>
                      </div>
                    )}
                  </div>

                  {item.change_reason && (
                    <div className="history-reason">
                      <strong>Motivo:</strong> {item.change_reason}
                    </div>
                  )}

                  {item.changed_by && (
                    <div className="history-author">
                      Alterado por: <strong>{item.changed_by.name}</strong>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <FormModal
        open={showEditModal && !!selectedPlatform}
        onClose={() => setShowEditModal(false)}
        title={
          selectedPlatform ? (
            <>
              {getPlatformIcon(selectedPlatform.platform)}{' '}
              Editar Versão {getPlatformName(selectedPlatform.platform)}
            </>
          ) : ''
        }
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowEditModal(false)}
              disabled={editLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              form="app-version-edit-form"
              variant="primary"
              loading={editLoading}
              disabled={editLoading}
            >
              {editLoading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </>
        }
      >
        <form id="app-version-edit-form" onSubmit={handleEditSubmit}>
          <div className="app-version-edit-form">
                {editError && (
                  <div className="form-error">
                    <Warning sx={{ fontSize: 18 }} />
                    {editError}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Versão Atual *</label>
                    <input
                      type="text"
                      value={editForm.current_version}
                      onChange={(e) => handleEditChange('current_version', e.target.value)}
                      placeholder="Ex: 1.4.0"
                      required
                    />
                    <span className="form-hint">Versão publicada nas lojas</span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Versão Mínima Obrigatória *</label>
                    <input
                      type="text"
                      value={editForm.minimum_version}
                      onChange={(e) => handleEditChange('minimum_version', e.target.value)}
                      placeholder="Ex: 1.2.0"
                      required
                    />
                    <span className="form-hint">Usuários com versão anterior serão obrigados a atualizar</span>
                  </div>
                  <div className="form-group">
                    <label>Versão Recomendada *</label>
                    <input
                      type="text"
                      value={editForm.recommended_version}
                      onChange={(e) => handleEditChange('recommended_version', e.target.value)}
                      placeholder="Ex: 1.3.5"
                      required
                    />
                    <span className="form-hint">Usuários serão incentivados a atualizar</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Changelog</label>
                  <textarea
                    value={editForm.changelog}
                    onChange={(e) => handleEditChange('changelog', e.target.value)}
                    placeholder="- Nova funcionalidade X&#10;- Correção de bugs&#10;- Melhorias de performance"
                    rows={5}
                  />
                  <span className="form-hint">Notas de atualização exibidas para o usuário</span>
                </div>

                <div className="form-group">
                  <label>Motivo da Alteração</label>
                  <input
                    type="text"
                    value={editForm.change_reason}
                    onChange={(e) => handleEditChange('change_reason', e.target.value)}
                    placeholder="Ex: Lançamento de nova versão com recursos de chat"
                  />
                  <span className="form-hint">Registrado no histórico para auditoria</span>
                </div>
          </div>
        </form>
      </FormModal>
    </div>
  );
};

export default AppVersionsPage;
